import React, { useState } from 'react';
import { XIcon, PlusIcon, GlobeIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { Badge } from 'src/components/ui/badge';
import { useKnownDomains } from 'src/api/organization/useKnownDomains';
import { useUpdateKnownDomains } from 'src/api/organization/useUpdateKnownDomains';

export function KnownDomainsCard() {
  const { data, isLoading, error } = useKnownDomains();
  const updateKnownDomains = useUpdateKnownDomains();

  const [isEditing, setIsEditing] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');

  const handleEdit = () => {
    setDomains(data?.knownDomains ?? []);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewDomain('');
  };

  const handleSave = async () => {
    try {
      // Add any pending domain from the input before saving
      const pendingDomain = newDomain.trim().toLowerCase();
      const shouldAddPending =
        pendingDomain &&
        !domains.includes(pendingDomain) &&
        pendingDomain.includes('.') &&
        !pendingDomain.includes('@') &&
        !pendingDomain.includes('/');

      const finalDomains = shouldAddPending
        ? [...domains, pendingDomain]
        : domains;

      await updateKnownDomains.mutateAsync({ knownDomains: finalDomains });
      setIsEditing(false);
      setNewDomain('');
    } catch (error) {
      console.error('Failed to update known domains:', error);
    }
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (domain && !domains.includes(domain)) {
      // Basic domain validation
      if (
        domain.includes('.') &&
        !domain.includes('@') &&
        !domain.includes('/')
      ) {
        setDomains([...domains, domain]);
        setNewDomain('');
      }
    }
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setDomains(domains.filter((d) => d !== domainToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <p className="text-red-600">Failed to load known domains</p>
        </CardContent>
      </Card>
    );
  }

  const currentDomains = data?.knownDomains ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GlobeIcon className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Known Domains</CardTitle>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={handleEdit}>
              Edit
            </Button>
          )}
        </div>
        <CardDescription>
          Email domains that belong to your organization. Users with these
          domains will use SSO authentication. Users with other domains will
          receive temporary passwords when invited.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="example.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleAddDomain}
                disabled={!newDomain.trim()}
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 min-h-[32px]">
              {domains.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No domains configured. Add domains above.
                </p>
              ) : (
                domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="secondary"
                    className="flex items-center gap-1 pl-2 pr-1 py-1"
                  >
                    {domain}
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className="ml-1 rounded-full hover:bg-muted p-0.5"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={updateKnownDomains.isPending}
              >
                {updateKnownDomains.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 min-h-[32px]">
            {currentDomains.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No known domains configured. Click Edit to add domains.
              </p>
            ) : (
              currentDomains.map((domain) => (
                <Badge key={domain} variant="secondary">
                  {domain}
                </Badge>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
